import axios from "../config/axios";
import qs from "qs";
import { callAPI } from "../api/calls"; // Import callAPI

interface LoginResponse {
  access_token: string;
  token_type: string;
  onboarding_complete?: boolean;
  profile_type?: string;
  subscription_status?: string;
  recording_limits?: any;
  subscription_details?: any;
}

interface SignupRequest {
  email: string;
  username: string;
  password: string;
}

interface RequestPasswordResetResponse {
  message: string;
}

interface ResetPasswordResponse {
  message: string;
}

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      console.log("authService.login - Starting login with:", username);
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);

      console.log("authService.login - Sending request to /api/scribing/auth/token");
      const response = await axios.post("/api/scribing/auth/token", formData);

      console.log("authService.login - Received response:", response.status);
      const data = response.data;
      console.log("authService.login - Response data:", data);
      
      console.log("authService.login - Storing token in localStorage");
      localStorage.setItem("token", data.access_token);

      // Log the onboarding status and store it in localStorage
      if (data.onboarding_complete !== undefined) {
        console.log("authService.login - API returned onboarding status:", data.onboarding_complete);
        // Store as string "true" or "false" for consistent handling
        const onboardingStatus = data.onboarding_complete === true ? "true" : "false";
        localStorage.setItem("onboarding_complete", onboardingStatus);
        console.log("authService.login - Stored onboarding status in localStorage:", onboardingStatus);

        // Dispatch event to notify listeners about the localStorage change
        window.dispatchEvent(new Event('localStorageUpdated'));
      }

      // Store profile_type if available
      if (data.profile_type) {
        console.log("authService.login - API returned profile_type:", data.profile_type);
        localStorage.setItem("profile_type", data.profile_type);
        console.log("authService.login - Stored profile_type in localStorage:", data.profile_type);
      }

      // Store full user profile data for quick access
      const profileData = {
        profile_type: data.profile_type,
        subscription_status: data.subscription_status,
        recording_limits: data.recording_limits,
        subscription_details: data.subscription_details
      };
      localStorage.setItem("user_profile", JSON.stringify(profileData));

      // Verify token was set correctly
      const storedToken = localStorage.getItem("token");
      console.log("authService.login - Verification: token stored correctly:", !!storedToken);

      return data;
    } catch (error: any) {
      console.error("authService.login - Error during login:", error);
      
      let errorMessage = "Login failed. Please check your credentials and try again.";
      
      // Check if the server returned a specific error message
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  async signup(email: string, password: string): Promise<void> {
    try {
      const signupData: SignupRequest = {
        email,
        username: email,
        password,
      };

      try {
        await axios.post("api/scribing/auth/signup", signupData, {
          headers: {
            accept: "application/json",
          },
        });

        // Login after successful signup
        await this.login(email, password);
      } catch (apiError: any) {
        // Handle axios error specifically
        if (apiError.response) {
          const status = apiError.response.status;
          const errorData = apiError.response.data;

          if (errorData?.detail) {
            throw new Error(
              typeof errorData.detail === "string"
                ? errorData.detail
                : "Signup failed. Please try again."
            );
          }

          throw new Error(
            `Signup failed with status ${status}: ${
              typeof errorData === "string"
                ? errorData
                : JSON.stringify(errorData)
            }`
          );
        }

        // If no response, it's likely a network error
        throw new Error(
          `Signup failed: ${apiError.message || "Network error"}`
        );
      }
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  },

  logout(redirectUrl = "/login") {
    // Clean up the Twilio device to prevent API calls after logout
    try {
      console.log("Destroying Twilio device before logout...");
      callAPI.destroy();
    } catch (error) {
      console.error("Error destroying Twilio device:", error);
    }
    
    localStorage.removeItem("token");
    localStorage.removeItem("onboarding_complete");
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("profile_type");
    localStorage.removeItem("user_profile");
    localStorage.removeItem("subscription_details");
    
    // Force a complete page refresh to reset all state
    window.location.href = redirectUrl;
  },

  getToken() {
    return localStorage.getItem("token");
  },

  isAuthenticated() {
    const token = this.getToken();
    console.log("authService.isAuthenticated - Token exists:", !!token);
    return !!token;
  },



  // Request a password reset code
  async requestPasswordReset(email: string): Promise<RequestPasswordResetResponse> {
    try {
      console.log("authService.requestPasswordReset - Requesting password reset for:", email);
      
      const response = await axios.post("/api/scribing/auth/forgot-password", {
        email: email
      }, {
        headers: {
          "Content-Type": "application/json"
        }
      });
      
      console.log("authService.requestPasswordReset - Response:", response.status);
      return response.data;
    } catch (error: any) {
      console.error("authService.requestPasswordReset - Error:", error);
      
      let errorMessage = "Failed to request password reset. Please try again.";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  // Reset password with verification code
  async resetPassword(email: string, code: string, password: string): Promise<ResetPasswordResponse> {
    try {
      console.log("authService.resetPassword - Resetting password for:", email);
      
      const response = await axios.post("/api/scribing/auth/reset-password", {
        email: email,
        code: code,
        new_password: password
      }, {
        headers: {
          "Content-Type": "application/json"
        }
      });
      
      console.log("authService.resetPassword - Response:", response.status);
      return response.data;
    } catch (error: any) {
      console.error("authService.resetPassword - Error:", error);
      
      let errorMessage = "Failed to reset password. Please check your verification code and try again.";
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      throw new Error(errorMessage);
    }
  }
};
